import type { NextConfig } from 'next';
import { displayLocalProxyInfo } from '../../bin/check-proxy';
import { MicrofrontendsServer } from '../../config/microfrontends/server';
import { getApplicationContext } from '../../config/microfrontends/utils/get-application-context';
import { transforms } from './transforms';
import { setEnvironment } from './env';
import type { WithMicrofrontendsOptions } from './types';

function typedEntries<T extends Record<string, unknown>>(
  obj: T,
): [keyof T, T[keyof T]][] {
  return Object.entries(obj) as [keyof T, T[keyof T]][];
}

function isProduction(opts?: WithMicrofrontendsOptions): boolean {
  if (opts?.isProduction) {
    return opts.isProduction();
  }

  return process.env.VERCEL_ENV === 'production';
}

/**
 * A function that wraps the next.config.js file to automatically configure zones
 * based on the microfrontends configuration.
 */
export function withMicrofrontends(
  nextConfig: NextConfig,
  opts?: WithMicrofrontendsOptions,
): NextConfig {
  if (opts?.debug) {
    process.env.MFE_DEBUG = 'true';
  }
  const { name: fromApp } = getApplicationContext(opts);
  const microfrontends = MicrofrontendsServer.infer({
    filePath: opts?.configPath,
  });

  // fetch the config for the current app
  const app = microfrontends.config.getApplication(fromApp);

  // configure the environment
  setEnvironment({ app, microfrontends });

  let next = { ...nextConfig };

  for (const [key, transform] of typedEntries(transforms)) {
    if (opts?.skipTransforms?.includes(key)) {
      // eslint-disable-next-line no-console
      console.log(`Skipping ${key} transform`);
      continue;
    }

    try {
      const transformedConfig = transform({
        app,
        next,
        microfrontend: microfrontends.config,
        opts: {
          isProduction: isProduction(opts),
          supportPagesRouter: opts?.supportPagesRouter,
        },
      });
      next = transformedConfig.next;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error transforming next config', e);
      // Fail the build
      throw e;
    }
  }

  displayLocalProxyInfo(microfrontends.config.getLocalProxyPort());
  return next;
}
